import React, { useMemo } from 'react';
import { useInvoices, Invoice } from '../../../hooks/useInvoices';
import { useTheme } from '../../../contexts/ThemeContext';

export interface RevenueByClientChartProps {
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
  limit?: number;
}

const RevenueByClientChart: React.FC<RevenueByClientChartProps> = ({
  embedded,
  settings,
  limit = 8,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { invoices = [], isLoading } = useInvoices();
  const { colors } = useTheme();

  const chartData = useMemo(() => {
    const arr = Array.isArray(invoices) ? invoices : [];
    const byClient: Record<string, number> = {};
    arr.forEach((inv: Invoice) => {
      const income = inv.invoiceIncome ?? 0;
      if (income <= 0) return;
      const name = inv.client?.clientName || `Client #${inv.clientId}`;
      byClient[name] = (byClient[name] || 0) + income;
    });
    return Object.entries(byClient)
      .map(([name, revenue]) => ({ name, revenue }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, effectiveLimit);
  }, [invoices, effectiveLimit]);

  const cardClass = embedded
    ? 'min-w-0'
    : `${colors.surface} rounded-xl shadow-sm p-5 ${colors.border} border`;

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-4`}>
          Revenue by Client
        </h2>
        <div className="h-48 flex items-center justify-center">
          <div className={`animate-pulse ${colors.textMuted}`}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-4`}>
          Revenue by Client
        </h2>
        <div className="h-48 flex items-center justify-center">
          <p className={colors.textMuted}>No revenue data</p>
        </div>
      </div>
    );
  }

  const maxRevenue = Math.max(...chartData.map((d) => d.revenue), 1);

  return (
    <div className={cardClass}>
      <h2 className={`text-base font-semibold ${colors.text} mb-4`}>
        Revenue by Client
      </h2>
      <div className="flex flex-col gap-2">
        {chartData.map((d) => (
          <div key={d.name} className="flex items-center gap-3">
            <span
              className={`text-xs flex-shrink-0 truncate max-w-[100px] sm:max-w-[140px] ${colors.text}`}
              title={d.name}
            >
              {d.name}
            </span>
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <div
                className="h-5 rounded bg-emerald-500 dark:bg-emerald-600 flex-shrink-0 transition-all"
                style={{
                  width: `${Math.max(4, (d.revenue / maxRevenue) * 100)}%`,
                  minWidth: d.revenue > 0 ? 8 : 0,
                }}
              />
              <span className={`text-xs ${colors.text} flex-shrink-0`}>
                €{d.revenue.toLocaleString('en-US', { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RevenueByClientChart;
